import React, { useState, useEffect } from "react";

import classnames from "classnames";
import {
  useGetShortTermDisabilityPlansQuery,
  useGetLongTermDisabilityPlansQuery,
  useGetCurrentCompanyApplicationQuery,
  useCreateDisabilityQuoteMutation,
  shortTermDisabilityPlansApi,
  longTermDisabilityPlansApi,
} from "EmployerApplication/api";
import isIntuit from "Intuit/isIntuit";
import { useDispatch, useSelector } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import DisabilityPlanCard from "../DisabilityPlanCard";
import { Body1, SubBody, LabelSmall } from "Cobalt/BodyText";
import Button from "Cobalt/Button";
import { Collapse } from "Cobalt/Collapse";
import { ModalBody, ModalFooter } from "Cobalt/Modal";
import { Card, CardSection } from "Common/Card";
import { getCart, addPlanToCart } from "Common/CartService";
import { formatFormValuesAsEmployeeProfile } from "Common/EnterAges/Components/Helpers/FormHelpers";
import { Row, Col } from "Common/Grid";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";
import { LoadingContainer } from "Common/LoadingContainer";
import { SizeSwitch } from "Common/Responsive";

import makeBem from "Utils/makeBem";

const bem = makeBem("AddAncillaryPlanModal");

const IncompleteCensusBanner = ({ setCurrentPage, previousPage }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <Card className={bem("IncompleteStatusBanner")}>
      <CardSection onClick={() => setIsExpanded(!isExpanded)} p={2} pl={0}>
        <Body1 weight="bold">
          <Icon icon="exclamation-triangle" size="xl" className={bem("warningIcon")} mr={3} ml={0} />
          You are viewing a list of estimated quotes
          <Icon icon={isExpanded ? "angle-up" : "angle-down"} size="xl" className="float-right" px={3} />
        </Body1>
      </CardSection>
      <Collapse in={isExpanded}>
        <Div className={bem("IncompleteStatusBannerText")} pl={4}>
          <SubBody pb={4} px={4}>
            For this quote estimation, we have assumed an average salary of $60,000 and an even split between male and female employees.
          </SubBody>
          <SubBody pb={3} px={4}>
            To get a more representative quote, please fill out accurate salary and gender information for your employees.
          </SubBody>
          <Button color="tertiary" className={bem("IncompleteStatusBannerButton")} onClick={() => setCurrentPage(previousPage)}>
            Enter information
          </Button>
        </Div>
      </Collapse>
    </Card>
  );
};

const LongTermDisabilityHeaderText = () => (
  <Body1 mb={4}>
    Long term disability replaces a portion of your employees’ income for a long period of time if they cannot work due to injury or illness.
  </Body1>
);

const ShortTermDisabilityHeaderText = () => (
  <Body1 mb={4}>
    Short term disability replaces a portion of your employees’ income for a short period of time if they cannot work due to injury or illness.
  </Body1>
);

export const PlansPage = ({ setCurrentPage, onHide, disabilityInsuranceType }) => {
  const previousPage = "salaryPage";
  const nextPage = "donePage";
  const pageNumber = 4;
  const totalPages = 4;

  const { data: companyApplication } = useGetCurrentCompanyApplicationQuery();
  const companyIDToken = companyApplication?.companyIDToken;

  const { data: shortTermDisabilityPlans, isFetching: isFetchingShortTermPlans } = useGetShortTermDisabilityPlansQuery(
    {
      companyIDToken,
    },
    { skip: disabilityInsuranceType !== "short-term" },
  );

  const { data: longTermDisabilityPlans, isFetching: isFetchingLongTermPlans } = useGetLongTermDisabilityPlansQuery(
    {
      companyIDToken,
    },
    { skip: disabilityInsuranceType !== "long-term" },
  );

  const employeeProfile = useSelector((state) => state.employeeProfile);
  const formattedEmployeeProfile = formatFormValuesAsEmployeeProfile(employeeProfile);

  const plans = disabilityInsuranceType === "short-term" ? shortTermDisabilityPlans?.results : longTermDisabilityPlans?.results;

  const [incompleteCensus, setIncompleteCensus] = useState(false);

  useEffect(() => {
    const fullTimeEmployees = formattedEmployeeProfile?.filter((employee) => employee.full_time);

    setIncompleteCensus(!fullTimeEmployees.every((employee) => !!employee.salary && !!employee.gender && !!employee.zip_code && !!employee.age));
  }, [employeeProfile]);

  const showIncompleteCensusBanner = incompleteCensus;

  const { short_term_disability: shortTermPlanId, long_term_disability: longTermPlanId } = useSelector(getCart);

  const planInCartID = disabilityInsuranceType === "short-term" ? shortTermPlanId : longTermPlanId;

  const [createDisabilityQuote] = useCreateDisabilityQuoteMutation();
  const [selectedPlanID, setSelectedPlanID] = useState(planInCartID);

  const addDisabilityPlanHandler = (plan) => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      label: "disability_plan_selected",
      value: plan,
    });

    setSelectedPlanID(plan.id);
    dispatch(addPlanToCart({ planType: plan.type, plan }));
    if (incompleteCensus) {
      AnalyticsTracking.trackEvent("disability_returned_to_census");
      return setCurrentPage(previousPage);
    }
  };

  const dispatch = useDispatch();

  const handleQuoteCreation = async () => {
    if (selectedPlanID) {
      const plan = plans.find((plan) => plan.id === selectedPlanID);
      dispatch(addPlanToCart({ planType: plan.type, plan }));
      await createDisabilityQuote({
        companyIDToken,
        disabilityPlanID: selectedPlanID,
        displayedPrice: plan.average_monthly_premium,
      });

      if (disabilityInsuranceType === "short-term") {
        dispatch(shortTermDisabilityPlansApi.util.invalidateTags(["ShortTermDisabilityPlans"]));
      } else {
        dispatch(longTermDisabilityPlansApi.util.invalidateTags(["LongTermDisabilityPlans"]));
      }
    }
    setCurrentPage(nextPage);
  };

  return (
    <>
      <ModalBody className={isIntuit() ? classnames(bem("body-intuit"), bem("sicLifePage")) : classnames(bem("body"), bem("sicLifePage"))}>
        {showIncompleteCensusBanner ? (
          <IncompleteCensusBanner {...{ setCurrentPage, previousPage }} />
        ) : (
          <>{disabilityInsuranceType === "short-term" ? <ShortTermDisabilityHeaderText /> : <LongTermDisabilityHeaderText />}</>
        )}

        {isFetchingShortTermPlans || isFetchingLongTermPlans ? (
          <LoadingContainer />
        ) : (
          <Row>
            {plans?.map((plan) => (
              <DisabilityPlanCard
                key={plan.id}
                plan={plan}
                onHide={onHide}
                addDisabilityPlanHandler={addDisabilityPlanHandler}
                selectedPlanID={selectedPlanID}
              />
            ))}
          </Row>
        )}
      </ModalBody>
      <ModalFooter>
        <SizeSwitch
          sm={
            <Row className={bem("numberedFooter")}>
              {previousPage ? (
                <Button color="tertiary" className="pull-left" onClick={() => setCurrentPage(previousPage)}>
                  <Icon icon="chevron-left" pr={3} />
                  Back
                </Button>
              ) : null}
              <LabelSmall pl={0}>
                Step {pageNumber} / {totalPages}
              </LabelSmall>
              {incompleteCensus ? (
                <Button color="primary" className="float-right" onClick={() => setCurrentPage(previousPage)}>
                  Enter information
                </Button>
              ) : (
                <Button color="primary" className="float-right" onClick={handleQuoteCreation} disabled={!selectedPlanID || !planInCartID}>
                  Continue
                </Button>
              )}
            </Row>
          }
          xs={
            <div className={bem("mobileFooter")}>
              <Button color="white" fullWidth onClick={() => setCurrentPage(previousPage)}>
                Back
              </Button>
              {incompleteCensus ? (
                <Button color="primary" fullWidth onClick={() => setCurrentPage(previousPage)}>
                  Enter information
                </Button>
              ) : (
                <Button color="primary" fullWidth onClick={handleQuoteCreation} disabled={!selectedPlanID || !planInCartID}>
                  Continue
                </Button>
              )}
            </div>
          }
        />
      </ModalFooter>
    </>
  );
};
